/**
 * RC-040 — "Ligado" não pode significar duas coisas.
 *
 * Um modelo ligado que exige uma variável que a clínica não preencheu FALHA em toda
 * emissão, em silêncio. A tela dizia "Ligado" e a frase ao lado prometia que "toda
 * venda de pacote passa a gerar o seu" — as duas coisas falsas ao mesmo tempo.
 *
 * Aqui mora a pergunta que faltava: com o que a clínica tem hoje, este modelo
 * consegue ser emitido? A resposta é a mesma regra do servidor (allowlist de
 * `document-variables.ts`), do lado de cá, para a tela poder avisar ANTES.
 *
 * Só entram variáveis que dependem de CADASTRO DA CLÍNICA. As da paciente
 * (`paciente.*`) são resolvidas no ato da emissão, uma a uma, e não se sabe de
 * antemão; `pacote.itens` e `data` vêm do próprio gatilho.
 */

export interface ClinicDataForDocuments {
  nome?: string | null;
  cnpj?: string | null;
}

interface RequisitoDaClinica {
  variavel: string;
  /** O que dizer que falta, na língua de quem lê a tela. */
  rotulo: string;
  presente: (clinica: ClinicDataForDocuments) => boolean;
}

const REQUISITOS: RequisitoDaClinica[] = [
  {
    variavel: 'clinica.nome',
    rotulo: 'o nome da clínica',
    presente: (c) => Boolean(c.nome && c.nome.trim()),
  },
  {
    variavel: 'clinica.cnpj',
    rotulo: 'o CNPJ da clínica',
    presente: (c) => Boolean(c.cnpj && c.cnpj.trim()),
  },
];

/**
 * O que impede este modelo de ser emitido agora. Lista vazia = está pronto.
 * `variables` é a lista que o próprio servidor calcula por modelo na listagem.
 */
export function faltaParaEmitir(
  variables: string[] | undefined,
  clinica: ClinicDataForDocuments | undefined,
): string[] {
  if (!variables?.length) return [];
  const dados = clinica ?? {};
  return REQUISITOS.filter((r) => variables.includes(r.variavel) && !r.presente(dados)).map(
    (r) => r.rotulo,
  );
}

/**
 * A frase pronta, já concordada em número: "falta o CNPJ da clínica" /
 * "faltam o nome da clínica e o CNPJ da clínica". Devolve null quando não falta nada,
 * para o chamador poder usar direto num `&&`.
 */
export function faltaParaEmitirLabel(
  variables: string[] | undefined,
  clinica: ClinicDataForDocuments | undefined,
): string | null {
  const faltas = faltaParaEmitir(variables, clinica);
  if (faltas.length === 0) return null;
  if (faltas.length === 1) return `falta ${faltas[0]}`;
  return `faltam ${faltas.slice(0, -1).join(', ')} e ${faltas[faltas.length - 1]}`;
}
